'use client';

import { useState, useEffect } from 'react';
import { useI18n } from '@/app/components/LanguageProvider';
import { submitForm, resetForm } from '@/lib/form-utils';

// Ключи для ролей, которые мы будем переводить
const roleKeys: string[] = [
    "roleFrontend",
    "roleBackend",
    "roleMobile",
    "roleDevOps",
    "roleUiUx",
    "rolePm",
    "roleQa"
];

interface CareerFormProps {
    vacancyId?: number;
    vacancyTitle?: string;
}

export default function CareerForm({ vacancyId, vacancyTitle }: CareerFormProps) {
    const { t, loadComponentTranslations } = useI18n();
    const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

    useEffect(() => {
        void loadComponentTranslations('CareerForm');
    }, [loadComponentTranslations]);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setStatus('submitting');

        const formData = new FormData(event.currentTarget);
        
        // Add vacancyId if provided
        if (vacancyId) {
            formData.append('vacancyId', vacancyId.toString());
        }
        
        // Add vacancyTitle as role if provided
        if (vacancyTitle) {
            formData.append('role', vacancyTitle);
        }
        
        const result = await submitForm('/api/careers/apply', formData);
        
        if (result.success) {
            setStatus('success');
            resetForm(event.currentTarget);
            
            // Reset success message after 5 seconds
            setTimeout(() => {
                setStatus('idle');
            }, 5000);
        } else {
            setStatus('error');
        }
    };

    const roles = roleKeys.map(key => t(key, 'CareerForm'));

    return (
        <div className="relative max-w-3xl mx-auto p-px rounded-3xl bg-linear-to-b from-white/20 to-transparent">
            <div className="relative p-8 md:p-12 glass-rounded-3xl glass-form">
                {status === 'success' ? (
                    <div className="text-center py-12">
                        <h3 className="text-2xl font-bold text-green-400 mb-2">{t('formSuccessTitle', 'CareerForm')}</h3>
                        <p className="text-neutral-300">{t('formSuccessMessage', 'CareerForm')}</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label htmlFor="fullName" className="block text-sm font-medium text-neutral-300 mb-2">{t('formFullName', 'CareerForm')}</label>
                                <input
                                    type="text"
                                    name="fullName"
                                    id="fullName"
                                    required
                                    className="w-full glass-input glass-rounded px-4 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none glass-transition"
                                />
                            </div>
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-neutral-300 mb-2">{t('formEmail', 'CareerForm')}</label>
                                <input
                                    type="email"
                                    name="email"
                                    id="email"
                                    required
                                    className="w-full glass-input glass-rounded px-4 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none glass-transition"
                                />
                            </div>
                        </div>

                        {!vacancyTitle && (
                            <div>
                                <label htmlFor="role" className="block text-sm font-medium text-neutral-300 mb-2">{t('formSelectRole', 'CareerForm')}</label>
                                <select
                                    name="role"
                                    id="role"
                                    required
                                    defaultValue=""
                                    className="w-full glass-input glass-rounded px-4 py-2.5 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none glass-transition appearance-none"
                                    style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%239ca3af' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em' }}
                                >
                                    <option value="" disabled>{t('formSelectRolePlaceholder', 'CareerForm')}</option>
                                    {roles.map((role, index) => (
                                        <option key={index} value={role}>{role}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div>
                            <label htmlFor="portfolio" className="block text-sm font-medium text-neutral-300 mb-2">{t('formPortfolioLink', 'CareerForm')}</label>
                            <input
                                type="url"
                                name="portfolio"
                                id="portfolio"
                                placeholder="https://github.com/your-profile"
                                className="w-full glass-input glass-rounded px-4 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none glass-transition"
                            />
                        </div>

                        <div>
                            <label htmlFor="coverLetter" className="block text-sm font-medium text-neutral-300 mb-2">{t('formCoverLetter', 'CareerForm')}</label>
                            <textarea
                                name="coverLetter"
                                id="coverLetter"
                                rows={5}
                                className="w-full glass-input glass-rounded px-4 py-2 text-white focus:ring-2 focus:ring-blue-500 focus:outline-none glass-transition"
                            ></textarea>
                        </div>

                        <div>
                            <label htmlFor="resume" className="block text-sm font-medium text-neutral-300 mb-2">{t('formAttachResume', 'CareerForm')}</label>
                            <input
                                type="file"
                                name="resume"
                                id="resume"
                                required
                                accept=".pdf,.doc,.docx"
                                className="w-full text-sm text-neutral-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-600/20 file:text-blue-300 hover:file:bg-blue-600/30"
                            />
                        </div>

                        <div className="text-center pt-4">
                            <button
                                type="submit"
                                disabled={status === 'submitting'}
                                className="inline-block bg-blue-600 text-white font-semibold px-10 py-3 rounded-lg hover:bg-blue-700 transition-colors duration-300 transform hover:scale-105 disabled:bg-gray-500 disabled:scale-100 disabled:cursor-not-allowed"
                            >
                                {status === 'submitting' ? t('formSubmitting', 'CareerForm') : t('formSubmitButton', 'CareerForm')}
                            </button>
                            {status === 'error' && <p className="text-red-400 mt-4">{t('formErrorMessage', 'CareerForm')}</p>}
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}